import { tallyTokens } from "../../theme.tsx";
import * as React from "react";
import { Box, IconButton, useTheme, useMediaQuery } from "@mui/material";
import { SvgIcon } from "@mui/material";

import { useFeatureFlag } from "../../hooks/useFeatureFlag.tsx";
import VoiceRecorder from "./VoiceRecorder.tsx";
import VoiceModeButton from "../voice/VoiceModeButton.tsx";
import SourcesButton from "./SourcesButton.tsx";
import StopIcon from "@mui/icons-material/Stop";
import { useVoiceStatus, useVoiceSurface } from "../../voice/voiceStatus.ts";
import VoiceControls from "../voice/VoiceControls.tsx";
import VoiceOrb from "../voice/VoiceOrb.tsx";

// Custom thin arrow component
const ThinArrowUp = (props: any) => (
  <SvgIcon {...props}>
    <path
      d="M12 20V5 M6 11l6-6 6 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SvgIcon>
);

type Props = {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (textToSubmit?: string) => void;
  onStop: () => void;
  placeholder: string;
  disabled?: boolean;
  errorMessage?: string;
  isCircleView?: boolean;
  /** No action row: the send button sits at the end of the text line.
   *  For visitors who are not signed in, who have no voice or sources. */
  inlineSend?: boolean;
};

const FIELD_MIN_HEIGHT = 24;
const FIELD_MAX_HEIGHT = 150;

export function SearchBarCompact({
  value,
  onChange,
  onSubmit,
  onStop,
  placeholder,
  disabled,
  errorMessage,
  isCircleView = false,
  inlineSend = false,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isVoiceAssistantEnabled = useFeatureFlag("voice_assistant");
  const fieldRef = React.useRef<HTMLTextAreaElement | null>(null);

  // In voice mode the send button gives way to the session's controls, and
  // the orb floats above. The circle view has no room for either, so only the
  // regular composer counts as somewhere the session is shown; with none on
  // screen, VoiceModeHost floats the controls itself.
  const voiceState = useVoiceStatus((status) => status.state);
  const inVoiceMode = voiceState !== "idle" && !isCircleView;
  useVoiceSurface(!isCircleView);

  // Grow with the content up to a cap, then scroll - as in the approved design.
  const resizeField = React.useCallback(() => {
    const field = fieldRef.current;
    if (!field || isCircleView) return;
    field.style.height = "auto";
    field.style.height = `${Math.min(field.scrollHeight, FIELD_MAX_HEIGHT)}px`;
  }, [isCircleView]);

  // Also runs when the field is cleared after submitting.
  React.useEffect(() => {
    resizeField();
  }, [value, resizeField]);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // Enter sends, as it always has. Shift+Enter adds a line instead.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  // The landing circle keeps the compact pill; the conversation composer is the
  // taller box from the approved design, with its actions on their own row.
  const pillHeight = 48;
  const iconSize = 34;
  const ring = 4;

  const fieldStyles = {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 15,
    fontFamily: "inherit",
    color: tallyTokens.ink,
    margin: 0,
    boxSizing: "border-box" as const,
    "&::placeholder": { color: tallyTokens.ink3, opacity: 1, fontSize: 15 },
    "&::-webkit-input-placeholder": {
      color: tallyTokens.ink3,
      opacity: 1,
      WebkitTextFillColor: tallyTokens.ink3,
    },
    "&::-moz-placeholder": { color: tallyTokens.ink3, opacity: 1 },
    "&:-ms-input-placeholder": { color: tallyTokens.ink3, opacity: 1 },
    "&:focus": { outline: "none" },
    WebkitAppearance: "none" as const,
    appearance: "none" as const,
  };

  const sharedFieldProps = {
    disabled,
    value,
    placeholder,
    onKeyDown: handleKeyDown,
    "aria-label": "Search",
    "data-testid": "search-input",
    autoComplete: "off",
    autoCorrect: "off",
    autoCapitalize: "off",
    spellCheck: false,
  };

  const sendButton = (
    <IconButton
      aria-label={disabled ? "Stop" : "Send"}
      onClick={disabled ? () => onStop() : () => onSubmit()}
      tabIndex={-1}
      sx={{
        m: 0,
        marginLeft: "auto",
        p: 0,
        flexShrink: 0,
        width: "31px",
        height: "31px",
        borderRadius: "999px",
        bgcolor: tallyTokens.brand,
        color: "#fff",
        "&:hover": {
          bgcolor: tallyTokens.brand,
          filter: "brightness(1.07)",
        },
      }}
    >
      {disabled ? (
        <StopIcon fontSize="small" />
      ) : (
        <ThinArrowUp fontSize="small" sx={{ color: "white" }} />
      )}
    </IconButton>
  );

  const trailingButton = inVoiceMode ? <VoiceControls /> : sendButton;

  if (isCircleView) {
    return (
      <Box
        sx={{
          position: "relative",
          width: { xs: "98%", sm: "95%", md: "92%", lg: "85%" },
          height: { xs: pillHeight - 6, sm: pillHeight },
          borderRadius: `${pillHeight / 2}px`,
          pl: { xs: "18px", sm: "22px" },
          bgcolor: tallyTokens.surface,
          border: `1px solid ${tallyTokens.lineStrong}`,
          boxShadow: tallyTokens.shadow.sm,
          transition: "border-color 140ms ease, box-shadow 140ms ease",
          "&:focus-within": {
            borderColor: tallyTokens.brand,
            boxShadow: tallyTokens.shadow.md,
          },
          display: "flex",
          alignItems: "center",
          boxSizing: "border-box",
        }}
      >
        <Box
          component="input"
          {...sharedFieldProps}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange(e.target.value)
          }
          sx={{
            ...fieldStyles,
            width: `calc(100% - ${iconSize + ring * 2 + 8}px)`,
            height: { xs: `${pillHeight - 10}px`, sm: `${pillHeight - 4}px` },
            lineHeight: 1.4,
            padding: "2px 0",
          }}
        />
        <IconButton
          aria-label={disabled ? "Stop" : "Send"}
          onClick={disabled ? () => onStop() : () => onSubmit()}
          tabIndex={-1}
          sx={{
            position: "absolute",
            right: 4,
            top: "50%",
            transform: "translateY(-50%)",
            m: 0,
            p: 0,
            width: { xs: iconSize - 10, sm: iconSize },
            height: { xs: iconSize - 10, sm: iconSize },
            borderRadius: "50%",
            bgcolor: tallyTokens.brand,
            color: "#fff",
            "&:hover": {
              bgcolor: tallyTokens.brand,
              filter: "brightness(1.07)",
            },
          }}
        >
          {disabled ? (
            <StopIcon fontSize={isMobile ? "small" : "medium"} />
          ) : (
            <ThinArrowUp
              fontSize={isMobile ? "small" : "medium"}
              sx={{ color: "white" }}
            />
          )}
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: `${tallyTokens.radius.lg}px`,
        padding: "11px 13px 9px",
        bgcolor: tallyTokens.surface,
        border: `1px solid ${tallyTokens.lineStrong}`,
        boxShadow: tallyTokens.shadow.md,
        transition: "border-color 140ms ease, box-shadow 140ms ease",
        "&:focus-within": {
          borderColor: tallyTokens.brand,
          boxShadow: tallyTokens.shadow.lg,
        },
        display: "flex",
        flexDirection: inlineSend ? "row" : "column",
        ...(inlineSend && {
          // One line tall: the text and the send button share a row, with the
          // button kept at the bottom as the text grows.
          alignItems: "flex-end",
          gap: "8px",
          padding: "9px 9px 9px 15px",
        }),
        boxSizing: "border-box",
      }}
    >
      {inVoiceMode && <VoiceOrb />}
      <Box
        component="textarea"
        rows={1}
        ref={fieldRef}
        {...sharedFieldProps}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
          onChange(e.target.value);
          resizeField();
        }}
        sx={{
          ...fieldStyles,
          width: "100%",
          minHeight: `${FIELD_MIN_HEIGHT}px`,
          maxHeight: `${FIELD_MAX_HEIGHT}px`,
          lineHeight: 1.5,
          // Centres a single line on the 31px send button beside it.
          padding: inlineSend ? "3.5px 0" : 0,
          resize: "none",
          overflowY: "auto",
          display: "block",
          scrollbarWidth: "thin",
        }}
      />

      {inlineSend ? (
        trailingButton
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            marginTop: "7px",
          }}
        >
          {/* Render Voice Recorder icon only when user logged-in and feature is enabled */}
          {isVoiceAssistantEnabled && (
            <VoiceRecorder disabled={disabled} compact />
          )}

          {/* Hands-free voice mode. Renders itself only when its own flags are on. */}
          <VoiceModeButton disabled={disabled} compact />

          {/* Which systems an answer can draw on. Hidden until the count loads. */}
          <SourcesButton disabled={disabled} />
          {trailingButton}
        </Box>
      )}
      {/* Absolutely positioned relative to the pill above, so an error (e.g. the
          concurrency-cap explanation) never shifts the pill's own layout/height. */}
      {errorMessage && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            mt: "6px",
            fontSize: 12,
            color: "error.main",
            textAlign: "center",
          }}
        >
          {errorMessage}
        </Box>
      )}
    </Box>
  );
}
